import type { OpenFundRequestDtoOut } from '@/models/out/OpenFundRequestDtoOut';
import type { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import type { ExistingDocumentsValue, ExistingDocumentRef } from '../ExistingDocumentsContext';
import { interpretWelfareCoordinators } from './welfareCoordinators';

const toRef = (doc?: DocumentDtoOut): ExistingDocumentRef | undefined =>
  doc?.uri ? { uri: doc.uri, name: doc.description || doc.name } : undefined;

/**
 * Builds the lookup of previously-uploaded documents from an existing request, so the completion
 * form can show each document with a link and allow replacing it (matched by form field path).
 * Uses the same visible-coordinator list as the form prefill so indices line up.
 */
export const buildExistingDocuments = (request: OpenFundRequestDtoOut): ExistingDocumentsValue => {
  const single: Record<string, ExistingDocumentRef> = {};
  const additional: Record<string, ExistingDocumentRef[]> = {};

  const { visibleCoordinators } = interpretWelfareCoordinators(request);

  visibleCoordinators.forEach((c, i) => {
    const idCard = toRef(c.idCardPhotocopy);
    if (idCard) single[`coordinators.${i}.idCardPhotocopy`] = idCard;
    const appendix = toRef(c.idAppendixPhotocopy);
    if (appendix) single[`coordinators.${i}.idAppendixPhotocopy`] = appendix;
    const extras = (c.additionalDocuments ?? []).map(toRef).filter((r): r is ExistingDocumentRef => !!r);
    if (extras.length) additional[`coordinators.${i}`] = extras;
  });

  (request.supporteds ?? []).forEach((s, i) => {
    const idCard = toRef(s.idCardPhotocopy);
    if (idCard) single[`supporteds.${i}.idCardPhotocopy`] = idCard;
    const appendix = toRef(s.idAppendixPhotocopy);
    if (appendix) single[`supporteds.${i}.idAppendixPhotocopy`] = appendix;
    const extras = (s.additionalDocuments ?? []).map(toRef).filter((r): r is ExistingDocumentRef => !!r);
    if (extras.length) additional[`supporteds.${i}`] = extras;
  });

  return { single, additional };
};
